// Reusable typographic + layout primitives for Continuum pages
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export function Overline({ children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`text-xs font-black uppercase tracking-[0.2em] text-slate-500 ${className}`}
    >
      {children}
    </div>
  );
}

export function Section({ children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`py-20 md:py-32 ${className}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">{children}</div>
    </section>
  );
}

export function PageHeader({ overline, title, lede, testid = "page-header", children }) {
  return (
    <section data-testid={testid} className="pt-24 pb-16 md:pt-32 md:pb-20 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {overline && <Overline className="mb-6">{overline}</Overline>}
        <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
          {title}
        </h1>
        {lede && (
          <p className="text-lg md:text-xl text-slate-600 mt-8 max-w-3xl leading-relaxed">
            {lede}
          </p>
        )}
        {children}
      </div>
    </section>
  );
}

export function PrimaryCTA({ to, children, testid, external = false }) {
  if (external) {
    return (
      <a
        href={to}
        data-testid={testid}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-[#FF8A00] text-[#050A15] font-bold px-6 py-3.5 hover:bg-[#FFA833] transition-colors inline-flex items-center gap-2 tracking-tight"
      >
        {children} <ArrowUpRight size={18} />
      </a>
    );
  }
  return (
    <Link
      to={to}
      data-testid={testid}
      className="bg-[#FF8A00] text-[#050A15] font-bold px-6 py-3.5 hover:bg-[#FFA833] transition-colors inline-flex items-center gap-2 tracking-tight"
    >
      {children} <ArrowUpRight size={18} />
    </Link>
  );
}

export function SecondaryCTA({ to, children, testid, dark = false }) {
  const cls = dark
    ? "bg-transparent border border-white/40 text-white hover:bg-white hover:text-[#0A1224]"
    : "bg-transparent border border-[#0A1224] text-[#0A1224] hover:bg-[#0A1224] hover:text-white";
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`${cls} font-bold px-6 py-3.5 transition-colors inline-flex items-center gap-2 tracking-tight`}
    >
      {children}
    </Link>
  );
}

export function TrustStrip() {
  const items = [
    { k: "Methodology", v: "Versioned & documented" },
    { k: "Data residency", v: "Canadian-resident" },
    { k: "Sign-off", v: "Named professional" },
    { k: "Pricing", v: "Published, transparent" },
  ];
  return (
    <div data-testid="trust-strip" className="border-y border-slate-200 py-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {items.map((it) => (
          <div key={it.k} className="border-l-2 border-[#FF8A00] pl-4">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
              {it.k}
            </div>
            <div className="text-[#0A1224] font-medium mt-1">{it.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StatLine({ items }) {
  return (
    <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4 tabular-nums">
      {items.map((it) => (
        <div key={it.label} className="border-l-2 border-[#FF8A00] pl-4">
          <dt className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
            {it.label}
          </dt>
          <dd className="text-[#0A1224] text-2xl md:text-3xl font-bold tracking-tight mt-1">
            {it.value}
          </dd>
          {it.note && <p className="text-xs text-slate-500 mt-1">{it.note}</p>}
        </div>
      ))}
    </dl>
  );
}
